import { TableColumnAlignment } from "atlas-table";

export interface Performance {
  date: string;
  data: PerformanceData;
}

export const PERFORMANCE_KEYS = [
  '1m',
  '3m',
  'CYTD',
  'FYTD',
  '1y',
  '3y',
  '5y',
  '7y',
  '10y'
];

export const PERFORMANCE_KEY_LABELS: PerformanceKeyLabels = {
  '1m': '1m',
  '1y': '1y',
  '3m': '3m',
  CYTD: 'CYTD',
  FYTD: 'FYTD',
  '3y': '3 (p.a.)',
  '5y': '5 (p.a.)',
  '7y': '7 (p.a.)',
  '10y': '10 (p.a.)'
};

export interface PerformanceKeyLabels {
  [key: string]: string;
}

export interface PerformancePortfolioTotal {
  '1m': PerformanceFundData;
  '1y': PerformanceFundData;
  '3m': PerformanceFundData;
  'CYTD': PerformanceFundData;
  'FYTD': PerformanceFundData;
  '3y': PerformanceFundData;
  '5y': PerformanceFundData;
  '7y': PerformanceFundData;
  '10y': PerformanceFundData;
  'fundsDistribution': PerformanceManager[];
  'isClientPerfDataAvailable': string;
}

export interface PerformanceData {
  funds: PerformanceFund[];
  total: PerformancePortfolioTotal;
}

export interface PerformanceFund {
  _id: string;
  name: string;
  benchmarkName?: string;
  managers: PerformanceManager[];
  '1m': PerformanceFundData;
  '1y': PerformanceFundData;
  '3m': PerformanceFundData;
  'CYTD': PerformanceFundData;
  'FYTD': PerformanceFundData;
  '3y': PerformanceFundData;
  '5y': PerformanceFundData;
  '7y': PerformanceFundData;
  '10y': PerformanceFundData;
}

export interface PerformanceManager {
  _id: string;
  name: string;
  code: string;
  '1m': PerformanceFundData;
  '1y': PerformanceFundData;
  '3m': PerformanceFundData;
  'CYTD': PerformanceFundData;
  'FYTD': PerformanceFundData;
  '3y': PerformanceFundData;
  '5y': PerformanceFundData;
  '7y': PerformanceFundData;
  '10y': PerformanceFundData;
}

export interface PerformanceTableRow {
  _id: string;
  parent?: string;
  tooltip?: string;
  selected: boolean;
  name: string;
  '1m': string;
  '1y': string;
  '3m': string;
  'CYTD': string;
  'FYTD': string;
  '3y': string;
  '5y': string;
  '7y': string;
  '10y': string;
}

export interface PerformanceFundData {
  net: string;
  netExcess: string;
  gross: string;
  grossExcess: string;
  benchmark: string;
}

export interface PerformanceFilter {
  type: PerformanceTypes;
  option: PerformanceOptions;
  fund?: string;
  month?: number;
  year?: number;
}

export interface PerformanceRequestPayload {
  account: string;
  year: string;
  month: string;
}

export interface PerformanceRequestCSVPayload {
  account: string;
  funds: string;
  year: string;
  month: string;
  type: string;
  option?: string;
}

export interface PerformanceDrildownData {
  name: string;
  y: number;
  [key: string]: string | number;
}

export interface PerformanceDrildown {
  id?: string;
  name: string;
  color?: string;
  data: ((string | number)[] | PerformanceDrildownData)[];
  [key: string]: any;
}

export enum Types {
  GROSS = 'gross',
  NET = 'net'
}

export type PerformanceTypes = 'gross' | 'net';

export enum Options {
  TOTAL = 'total',
  EXCESS = 'excess'
}

export type PerformanceOptions = 'total' | 'excess';

export const PERFORMANCE_COMMON_HEADERS = [
  {
    name: '1mth (%)',
    column: '1m',
    align: TableColumnAlignment.right,
    displayLabel: '1mth (%)',
    sortable: true,
    fieldKey: '1m',

  },
  {
    name: '3mth (%)',
    fieldKey: '3m',
    align: TableColumnAlignment.right,
    displayLabel: '3mth (%)',
    sortable: true,
    column: '3m',

  },
  {
    name: 'CYTD (%)',
    fieldKey: 'CYTD',
    column: 'CYTD',
    align: TableColumnAlignment.right,
    displayLabel: 'CYTD (%)',
    sortable: true,
  },
  {
    name: 'FYTD (%)',
    fieldKey: 'FYTD',
    column: 'FYTD',
    align: TableColumnAlignment.right,
    displayLabel: 'FYTD (%)',
    sortable: true,
  },
  {
    name: '1yr (%)',
    fieldKey: '1y',
    column: '1y',
    align: TableColumnAlignment.right,
    displayLabel: '1yr (%)',
    sortable: true,
  },
  {
    name: '3yr (% p.a.)',
    column: '3y',
    fieldKey: '3y',
    align: TableColumnAlignment.right,
    displayLabel: '3yr (% p.a.)',
    sortable: true,
  },
  {
    name: '5yr (% p.a.)',
    column: '5y',
    fieldKey: '5y',
    align: TableColumnAlignment.right,
    displayLabel: '5yr (% p.a.)',
    sortable: true,
  },
  {
    name: '7yr (% p.a.)',
    fieldKey: '7y',
    column: '7y',
    align: TableColumnAlignment.right,
    displayLabel: '7yr (% p.a.)',
    sortable: true,
  },
  {
    name: '10yr (% p.a.)',
    column: '10y',
    fieldKey: '10y',
    align: TableColumnAlignment.right,
    displayLabel: '10yr (% p.a.)',
    sortable: true,
  }
];

export const OVERVIEW_PERFORMANCE_COMMON_HEADERS = [
  {
    name: '1mth (%)',
    column: '1m',
    align: TableColumnAlignment.right,
    displayLabel: '1mth (%)',
    sortable: false,
    fieldKey: '1m',

  },
  {
    name: '3mth (%)',
    fieldKey: '3m',
    align: TableColumnAlignment.right,
    displayLabel: '3mth (%)',
    sortable: false,
    column: '3m',

  },
  {
    name: 'CYTD (%)',
    fieldKey: 'CYTD',
    column: 'CYTD',
    align: TableColumnAlignment.right,
    displayLabel: 'CYTD (%)',
    sortable: false,
  },
  {
    name: 'FYTD (%)',
    fieldKey: 'FYTD',
    column: 'FYTD',
    align: TableColumnAlignment.right,
    displayLabel: 'FYTD (%)',
    sortable: false,
  },
  {
    name: '1yr (%)',
    fieldKey: '1y',
    column: '1y',
    align: TableColumnAlignment.right,
    displayLabel: '1yr (%)',
    sortable: false,
  },
  {
    name: '3yr (% p.a.)',
    column: '3y',
    fieldKey: '3y',
    align: TableColumnAlignment.right,
    displayLabel: '3yr (% p.a.)',
    sortable: false,
  },
  {
    name: '5yr (% p.a.)',
    column: '5y',
    fieldKey: '5y',
    align: TableColumnAlignment.right,
    displayLabel: '5yr (% p.a.)',
    sortable: false,
  },
  {
    name: '7yr (% p.a.)',
    fieldKey: '7y',
    column: '7y',
    align: TableColumnAlignment.right,
    displayLabel: '7yr (% p.a.)',
    sortable: false,
  },
  {
    name: '10yr (% p.a.)',
    column: '10y',
    fieldKey: '10y',
    align: TableColumnAlignment.right,
    displayLabel: '10yr (% p.a.)',
    sortable: false,
  }
];
